// Pure helpers for the in-conversation keyword search ("search this chat").
// Kept free of React/DOM so they can be unit-tested in isolation and reused by
// both the message list (match computation) and message rendering (highlight).

type SearchableMessage = {
    readonly id: string;
    readonly content: string;
    // Inbound non-English messages also render an English translation block;
    // it's visible text, so the keyword search includes it.
    readonly translation?: {
        readonly translated_text?: string | null;
    } | null;
};

export type SearchTextSegment = {
    readonly text: string;
    readonly isMatch: boolean;
};

/**
 * Normalize a raw search input into a comparable keyword. Returns an empty
 * string when there is nothing meaningful to search for, which callers treat
 * as "no active search".
 */
export function normalizeSearchQuery(query: string): string {
    return query.trim().toLowerCase();
}

/**
 * Return the ids of the messages whose content contains the keyword, preserving
 * the input order (the message list is chronological, so matches come back
 * oldest-first). A blank query yields no matches.
 */
export function findConversationSearchMatches(
    messages: ReadonlyArray<SearchableMessage>,
    query: string,
): Array<string> {
    const normalizedQuery = normalizeSearchQuery(query);
    if (!normalizedQuery) {
        return [];
    }

    const matchIds: Array<string> = [];
    for (const message of messages) {
        const haystacks = [
            message.content,
            message.translation?.translated_text ?? '',
        ];
        if (
            haystacks.some((text) =>
                text.toLowerCase().includes(normalizedQuery),
            )
        ) {
            matchIds.push(message.id);
        }
    }
    return matchIds;
}

/**
 * Split text into ordered segments marking which parts match the keyword, so a
 * renderer can wrap matches without using `dangerouslySetInnerHTML`. Matching is
 * case-insensitive but the original casing of the text is preserved. A blank
 * query returns the whole text as a single non-matching segment.
 */
export function splitTextBySearchQuery(
    text: string,
    query: string,
): Array<SearchTextSegment> {
    const normalizedQuery = normalizeSearchQuery(query);
    if (!normalizedQuery || !text) {
        return [{ text, isMatch: false }];
    }

    const segments: Array<SearchTextSegment> = [];
    const lowerText = text.toLowerCase();
    const queryLength = normalizedQuery.length;
    let cursor = 0;

    while (cursor < text.length) {
        const matchIndex = lowerText.indexOf(normalizedQuery, cursor);
        if (matchIndex === -1) {
            segments.push({ text: text.slice(cursor), isMatch: false });
            break;
        }
        if (matchIndex > cursor) {
            segments.push({
                text: text.slice(cursor, matchIndex),
                isMatch: false,
            });
        }
        segments.push({
            text: text.slice(matchIndex, matchIndex + queryLength),
            isMatch: true,
        });
        cursor = matchIndex + queryLength;
    }

    return segments;
}
